<template>
  <card>
    <h4 class="card-title">{{$t('Employees')}}</h4>
    <el-button
      type="primary"
      size="medium"
      @click.native="toggleModal(null)"
      class="add-button"
    >{{ $t("Add an employee") }}</el-button>
    <div class="row equal-height">
      <div
        v-for="employee in employees"
        :key="employee.record_id"
        class="col-md-6 col-lg-4 employee"
        @click="(e) => {
          if (e.target.tagName === 'A') {
            return
          }
          toggleModal(employee)
        }"
      >
        <card
          no-footer-line
        >
          <div slot="header" class="employee-header">
            <h5>{{ employee.fullname }}</h5>
            <h6 class="card-subtitle text-muted position">{{ employee.position }}</h6>
          </div>
          <div slot="default">
            <ul class="contacts">
              <li v-if="employee.phone" class="contact">
                <i class="now-ui-icons tech_mobile"></i>
                <a :href="`tel:${employee.phone}`">{{ employee.phone | formatPhone }}</a>
              </li>
              <li v-if="employee.email" class="contact">
                <i class="now-ui-icons ui-1_email-85"></i>
                <a :href="`mailto:${employee.email}`">{{ employee.email }}</a>
              </li>
            </ul>
          </div>
        </card>
      </div>
    </div>
    <el-dialog
      :visible.sync="modal"
      @opened="onOpenModal"
      width="500px"
      :fullscreen="$mq === 'xs'">
      <div slot="title">
        <h4 class="title title-up" v-text="$t(employeeModel.record_id ? 'Edit employee' : 'Add employee')"></h4>
      </div>
      <fg-input
        name="firstname"
        type="text"
        v-model="employeeModel.firstname"
        v-validate="'required'"
        :error="getError('firstname')"
      >
        <span slot="label">{{ $t('Name') }}</span>
      </fg-input>
      <fg-input
        name="lastname"
        type="text"
        v-model="employeeModel.lastname"
        :error="getError('lastname')"
      >
        <span slot="label">{{ $t('Last name') }}</span>
      </fg-input>
      <fg-input
        name="middlename"
        type="text"
        v-model="employeeModel.middlename"
        :error="getError('middlename')"
      >
        <span slot="label">{{ $t('Middle name') }}</span>
      </fg-input>
      <fg-input
        name="position"
        type="text"
        v-model="employeeModel.position"
        :error="getError('position')"
      >
        <span slot="label">{{ $t('Position') }}</span>
      </fg-input>
      <fg-input
        name="email"
        type="email"
        v-model="employeeModel.email"
        :error="getError('email')"
        @input="resetEmailFields"
      >
        <span slot="label">{{ $t('Email') }}</span>
      </fg-input>
      <fg-input
        name="phone"
        type="tel"
        id="employee-phone"
        v-model="employeeModel.phone"
        :error="getError('phone')"
        @input="resetSmsFields"
      >
        <span slot="label">{{ $t('Phone') }}</span>
      </fg-input>
      <div>
        <span style="display: inline-block; width: 160px; margin-right: 30px">{{$t('Receive notifications')}}</span>
        <el-checkbox  style="color: #55595c" v-model="employeeModel.send_mail" label="Email" :disabled="!employeeModel.email" ref="sendMail"></el-checkbox>
        <el-checkbox  style="color: #55595c" v-model="employeeModel.send_sms" label="SMS" :disabled="!employeeModel.phone" ref="sendSms"></el-checkbox>
      </div>
      <div>
        <span style="display: inline-block; width: 160px;margin-right: 30px">{{$t('Receive invoices')}}</span>
        <el-checkbox  style="color: #55595c" v-model="employeeModel.send_order" label="Email" :disabled="!employeeModel.email" ref="sendOrder"></el-checkbox>
      </div>
      <template slot="footer">
        <el-button v-if="employeeModel.record_id" type="danger" plain class="cancel-button" @click.native="confirmDeleteModal = true">{{ $t('Delete') }}</el-button>
        <el-button
          type="primary"
          @click.native="employeeModel.record_id ? updateEmployee() : addEmployee()"
        >
        {{ employeeModel.record_id ? $t('Update') : $t('Add') }}
        </el-button>
      </template>
    </el-dialog>
    <el-dialog
      :visible.sync="confirmDeleteModal"
      width="500px"
      :fullscreen="$mq === 'xs'">
      <div slot="title">
        <h4 class="title title-up">{{ $t('Delete')}}</h4>
      </div>
      <p>{{$t('Do you really want to delete employee?')}}</p>
      <template slot="footer">
        <el-button plain @click.native="confirmDeleteModal = false" class="cancel-button">{{ $t('Cancel') }}</el-button>
        <el-button type="danger" @click.native="removeEmployee">{{ $t('Delete') }}</el-button>
      </template>
    </el-dialog>
  </card>
</template>

<script>
import { Table, TableColumn, Button, Checkbox } from 'element-ui'
import { Modal, FormGroupInput as FgInput } from '@/ui'
import axios from 'axios'
import Inputmask from 'inputmask'

export default {
  name: 'Employees',
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Checkbox.name]: Checkbox,
    'el-button': Button,
    Modal,
    FgInput
  },
  props: ['phoneMask'],
  data() {
    return {
      employees: [],
      employeeModel: {
        record_id: null,
        firstname: '',
        lastname: '',
        middlename: '',
        position: '',
        email: '',
        phone: '',
        send_mail: false,
        send_sms: false,
        send_order: false
      },
      loading: false,
      modal: false,
      confirmDeleteModal: false
    }
  },
  mounted() {
    this.fetchEmployees()
  },
  methods: {
    onOpenModal: function() {
      var im = new Inputmask(this.phoneMask)
      im.mask(document.getElementById('employee-phone'))
    },
    addNew: function() {
      Object.assign(this.$data.employeeModel, this.$options.data().employeeModel)
      this.toggleModal()
    },
    fetchEmployees: function() {
      this.loading = true
      axios.get('employees').then(promise => {
        this.employees = promise.data.results
        this.loading = false
      }).catch(error => {
        console.log(error)
      })
    },
    toggleModal: function(employee = null) {
      if (employee) {
        this.employeeModel.record_id = employee.record_id
        this.employeeModel.firstname = employee.name
        this.employeeModel.lastname = employee.surname
        this.employeeModel.middlename = employee.patronymic
        this.employeeModel.position = employee.position
        this.employeeModel.email = employee.email
        this.employeeModel.phone = employee.phone
        this.employeeModel.send_mail = employee.send_mail
        this.employeeModel.send_sms = employee.send_sms
        this.employeeModel.send_order = employee.send_order
      } else {
        Object.assign(this.$data.employeeModel, this.$options.data().employeeModel)
      }
      this.$validator.reset()
      this.modal = !this.modal
    },
    addEmployee: function() {
      this.$validator.validateAll()
        .then((result) => {
          if (!result) {
            return
          }
          axios.post('employees/create', {
            ...this.employeeModel
          }).then(promise => {
            this.modal = false
            this.fetchEmployees()
            this.$notify(
              {
                message: this.$t('Employee created'),
                horizontalAlign: 'center',
                verticalAlign: 'top',
                type: 'success',
                timeout: 1500
              })
          }).catch(error => {
            console.error(error.response.data)
            this.modal = false
            this.$notify(
              {
                message: this.$t('An error occurred while creating employee. Please try later.'),
                horizontalAlign: 'center',
                verticalAlign: 'top',
                type: 'danger',
                timeout: 1500
              })
          })
        })
    },
    updateEmployee() {
      this.$validator.validateAll()
        .then((result) => {
          if (!result) {
            return
          }
          axios.put('employees/' + this.employeeModel.record_id, {
            ...this.employeeModel
          }).then(promise => {
            this.modal = false
            this.fetchEmployees()
            this.$notify(
              {
                message: this.$t('Employee updated'),
                horizontalAlign: 'center',
                verticalAlign: 'top',
                type: 'success',
                timeout: 1500
              })
          }).catch(error => {
            console.error(error.response.data)
            this.modal = false
            this.$notify(
              {
                message: this.$t('An error occurred while updating employee. Please try later.'),
                horizontalAlign: 'center',
                verticalAlign: 'top',
                type: 'danger',
                timeout: 1500
              })
          })
        })
    },
    removeEmployee() {
      axios.delete('employees/' + this.employeeModel.record_id).then(promise => {
        this.confirmDeleteModal = false
        this.modal = false
        this.fetchEmployees()
        this.$notify(
          {
            message: this.$t('Employee deleted'),
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'success',
            timeout: 1500
          })
      })
    },
    getError (fieldName) {
      return this.$t(this.errors.first(fieldName))
    },
    resetEmailFields(value) {
      if (value === '') {
        this.employeeModel.send_mail = false
        this.employeeModel.send_order = false
      }
    },
    resetSmsFields(value) {
      if (value === '') {
        this.employeeModel.send_sms = false
      }
    }
  },
  filters: {
    formatPhone: function(value) {
      return value.replace(/(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/, '+$1 ($2) $3-$4-$5')
    }
  }
}
</script>

<style scoped>
.card-title {
  margin-top: 30px;
  margin-bottom: 30px
}
/*
 * Row with equal height columns
 * --------------------------------------------------
 */
.equal-height,
.equal-height > [class*='col-'],
.equal-height .card {
  display: flex
}

</style>

<style scoped>
  .add-button {
    margin-bottom: 20px;
  }
  .card-pricing {
    height: 350px;
  }
  .position {
    font-weight: normal
  }
  .contacts {
    list-style-type: none;
    padding: 0;
  }
  .contact {
    display: flex;
  }
  .contact:not(:last-child) {
    margin-bottom: 8px;
  }

  .contact > i {
    margin-right: 10px;
    font-size: 14px;
    line-height: 1.5;
  }
  .employee {
    cursor: pointer;
  }
</style>
